import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Phone, MessageSquare,
  Mail, CheckSquare, AlertTriangle, Video, Clock,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
   My Day → Calendar (#146) — per-user month grid + selected-day agenda.

   LEFT  : month-at-a-glance (Day/Week/Month), per-day type markers, CRITICAL
           days highlighted, click a day to select it.
   RIGHT : selected-day agenda — a "Critical today" section, then items grouped
           by line/type (Appointments / Calls / SMS / Email / Internal to-dos),
           each row deep-linking to its contact. Video visits get a one-click
           Join button (gated by video:use).

   Data: crm:calendar:month drives the grid (one cheap fetch per visible month);
   crm:calendar:day drives the agenda. Both are per-user (session.userId).
   ═══════════════════════════════════════════════════════════════════════════ */

const TYPE_COLORS = {
  appointment: '#2E8B98',
  task: '#F26522',
  followup: '#256F7A',
  reminder: '#62BB46',
  scheduled_send: '#4FA038',
};

const LINES = [
  { key: 'appointment', label: 'Appointments', icon: CalendarIcon },
  { key: 'call', label: 'Calls', icon: Phone },
  { key: 'sms', label: 'SMS', icon: MessageSquare },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'todo', label: 'Internal to-dos', icon: CheckSquare },
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The browser's IANA zone (e.g. 'America/Tijuana'). Backend windows/groups days
// in this zone; storage stays UTC. Fall back to 'UTC' when unresolved.
function resolveTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// ── date helpers (local time; keys are 'YYYY-MM-DD') ─────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function toKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function todayKey() { return toKey(new Date()); }
function parseKey(k) { const [y, m, d] = k.split('-').map(Number); return new Date(y, m - 1, d); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// 42-cell (6-week) grid of Date objects covering the month of (year, month1-12).
function monthGridCells(year, month1) {
  const first = new Date(year, month1 - 1, 1);
  const start = addDays(first, -first.getDay()); // back up to Sunday
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}
function weekCells(selectedKey) {
  const d = parseKey(selectedKey);
  const start = addDays(d, -d.getDay());
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export default function CalendarView({ user, onOpenContact, onAppNavigate }) {
  const canVideo = (user?.permissions || []).includes('video:use');
  const today = todayKey();
  const tz = useMemo(() => resolveTz(), []);

  const [view, setView] = useState('month'); // month | week | day
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() + 1 }; });
  const [selectedDate, setSelectedDate] = useState(today);

  const [monthData, setMonthData] = useState(null);
  const [monthLoading, setMonthLoading] = useState(true);
  const [dayData, setDayData] = useState(null);
  const [dayLoading, setDayLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMonth = useCallback(async (year, month) => {
    setMonthLoading(true);
    try {
      const res = await window.zignagenixAPI.getCRMCalendarMonth(year, month, tz);
      setMonthData(res && res.days ? res.days : {});
    } catch (e) {
      setError(e?.message || 'Failed to load the calendar.');
      setMonthData({});
    } finally {
      setMonthLoading(false);
    }
  }, [tz]);

  const loadDay = useCallback(async (dateISO) => {
    setDayLoading(true);
    try {
      const res = await window.zignagenixAPI.getCRMCalendarDay(dateISO, tz);
      setDayData(res && Array.isArray(res.items) ? res : { date: dateISO, items: [], critical: [] });
    } catch (e) {
      setError(e?.message || 'Failed to load the day agenda.');
      setDayData({ date: dateISO, items: [], critical: [] });
    } finally {
      setDayLoading(false);
    }
  }, [tz]);

  useEffect(() => { loadMonth(cursor.year, cursor.month); }, [cursor.year, cursor.month, loadMonth]);
  useEffect(() => { loadDay(selectedDate); }, [selectedDate, loadDay]);

  // Keep the visible month in sync when a selected day falls outside it.
  useEffect(() => {
    const d = parseKey(selectedDate);
    if (d.getFullYear() !== cursor.year || d.getMonth() + 1 !== cursor.month) {
      setCursor({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }
  }, [selectedDate]);

  function stepMonth(delta) {
    setCursor((c) => {
      let m = c.month + delta, y = c.year;
      if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
      return { year: y, month: m };
    });
  }
  function stepBy(days) { setSelectedDate((k) => toKey(addDays(parseKey(k), days))); }

  const cells = useMemo(() => {
    if (view === 'week') return weekCells(selectedDate);
    if (view === 'day') return [parseKey(selectedDate)];
    return monthGridCells(cursor.year, cursor.month);
  }, [view, cursor.year, cursor.month, selectedDate]);

  const monthLabel = new Date(cursor.year, cursor.month - 1, 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const selectedLabel = parseKey(selectedDate)
    .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="cal-wrap">
      {error && (
        <div style={{ gridColumn: '1 / -1', background: '#F8D7DA', color: '#721C24', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ── LEFT: month-at-a-glance ─────────────────────────────────────── */}
      <div className="cal-month-panel my-day-card">
        <div className="cal-month-header">
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#F26522', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {parseKey(today).toLocaleDateString('en-US', { weekday: 'long' })}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#212529' }}>
              {parseKey(today).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          </div>
          <div className="my-day-scope-toggle" role="group" aria-label="Calendar granularity">
            {['day', 'week', 'month'].map((v) => (
              <button key={v} className={view === v ? 'active' : ''} onClick={() => setView(v)} aria-pressed={view === v}>
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="cal-nav">
          <button onClick={() => (view === 'month' ? stepMonth(-1) : stepBy(view === 'week' ? -7 : -1))} aria-label="Previous"><ChevronLeft size={16} /></button>
          <span className="cal-nav-label">{view === 'month' ? monthLabel : selectedLabel}</span>
          <button onClick={() => (view === 'month' ? stepMonth(1) : stepBy(view === 'week' ? 7 : 1))} aria-label="Next"><ChevronRight size={16} /></button>
        </div>

        {view !== 'day' && (
          <div className="cal-weekday-row">
            {WEEKDAYS.map((w) => <div key={w} className="cal-weekday">{w}</div>)}
          </div>
        )}

        <div className={`cal-grid cal-grid-${view}`} aria-busy={monthLoading}>
          {cells.map((d) => {
            const key = toKey(d);
            const cell = (monthData || {})[key];
            const inMonth = d.getMonth() + 1 === cursor.month || view !== 'month';
            const isToday = key === today;
            const isSelected = key === selectedDate;
            const classes = ['cal-cell'];
            if (!inMonth) classes.push('cal-cell-dim');
            if (isToday) classes.push('cal-cell-today');
            if (isSelected) classes.push('cal-cell-selected');
            if (cell?.hasCritical) classes.push('cal-cell-critical');
            return (
              <button
                key={key}
                className={classes.join(' ')}
                onClick={() => setSelectedDate(key)}
                aria-pressed={isSelected}
                aria-label={`${d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${cell ? `, ${cell.total} items` : ''}${cell?.hasCritical ? ', has critical items' : ''}`}
              >
                <span className="cal-cell-num">{d.getDate()}</span>
                {cell?.hasCritical && <AlertTriangle size={11} className="cal-cell-crit-icon" aria-hidden="true" />}
                {cell && cell.total > 0 && (
                  <span className="cal-cell-markers">
                    {Object.keys(cell.counts || {}).slice(0, 4).map((t) => (
                      <span key={t} className="cal-dot" style={{ background: TYPE_COLORS[t] || '#ADB5BD' }} />
                    ))}
                    {(view !== 'month') && <span className="cal-cell-total">{cell.total}</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT: selected-day agenda ──────────────────────────────────── */}
      <div className="cal-agenda-panel my-day-card">
        <div className="cal-agenda-header">
          <CalendarIcon size={16} color="#2E8B98" />
          <span>{selectedLabel}</span>
          <span className="cal-agenda-tz" title="Days are windowed and times shown in this timezone">
            <Clock size={11} /> Times shown in {tz}
          </span>
        </div>
        <DayAgenda
          data={dayData}
          loading={dayLoading}
          onOpenContact={onOpenContact}
          canVideo={canVideo}
          onAppNavigate={onAppNavigate}
          user={user}
        />
      </div>
    </div>
  );
}

// ── Selected-day agenda ──────────────────────────────────────────────────────
function DayAgenda({ data, loading, onOpenContact, canVideo, onAppNavigate, user }) {
  if (loading && !data) {
    return (
      <div aria-busy="true" aria-label="Loading agenda">
        {[0, 1, 2, 3].map((i) => <div key={i} className="my-day-skeleton" style={{ width: `${90 - i * 10}%` }} />)}
      </div>
    );
  }
  const items = (data && data.items) || [];
  if (!items.length) {
    return <p style={{ fontSize: 13, color: '#6C757D', textAlign: 'center', margin: '24px 0' }}>Nothing scheduled for this day.</p>;
  }
  const critical = items.filter((i) => i.critical);

  return (
    <div className="cal-agenda-body">
      {critical.length > 0 && (
        <div className="cal-agenda-group cal-agenda-critical">
          <div className="cal-agenda-group-title" style={{ color: '#DC3545' }}>
            <AlertTriangle size={13} /> Critical today <span className="my-day-count-badge" style={{ background: '#DC3545', color: '#fff' }}>{critical.length}</span>
          </div>
          {critical.map((it) => (
            <AgendaRow key={`crit-${it.kind}-${it.id}`} item={it} onOpenContact={onOpenContact} canVideo={canVideo} onAppNavigate={onAppNavigate} user={user} />
          ))}
        </div>
      )}

      {LINES.map(({ key, label, icon: Icon }) => {
        const rows = items.filter((i) => i.line === key);
        if (!rows.length) return null;
        return (
          <div key={key} className="cal-agenda-group">
            <div className="cal-agenda-group-title">
              <Icon size={13} color="#2E8B98" /> {label} <span className="my-day-count-badge">{rows.length}</span>
            </div>
            {rows.map((it) => (
              <AgendaRow key={`${it.kind}-${it.id}`} item={it} onOpenContact={onOpenContact} canVideo={canVideo} onAppNavigate={onAppNavigate} user={user} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function AgendaRow({ item, onOpenContact, canVideo, onAppNavigate, user }) {
  const clickable = !!(onOpenContact && item.contactId);
  const open = () => { if (clickable) onOpenContact(item.contactId); };
  const meta = item.meta || {};

  return (
    <div
      className={`cal-agenda-row${clickable ? ' my-day-row-clickable' : ''}${item.critical ? ' cal-row-critical' : ''}`}
      onClick={clickable ? open : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } } : undefined}
    >
      <div className="cal-row-time">
        {item.time
          ? <><Clock size={10} /> {fmtTime(item.time)}</>
          : <span className="cal-row-allday">All-day</span>}
      </div>
      <div className="cal-row-body">
        <div className="cal-row-title">
          {item.title}
          {meta.isVideoVisit && <Video size={12} color="#256F7A" aria-label="Video visit" style={{ marginLeft: 4, verticalAlign: 'middle' }} />}
        </div>
        {item.contactName && <div className="cal-row-sub">{item.contactName}</div>}
        {(meta.reason || meta.attendees) && (
          <div className="cal-row-context">
            {meta.reason}{meta.reason && meta.attendees ? ' · ' : ''}{meta.attendees ? `with ${meta.attendees}` : ''}
          </div>
        )}
      </div>
      {item.priority && (item.priority === 'urgent' || item.priority === 'high') && (
        <span className="cal-row-pri" style={{ color: item.priority === 'urgent' ? '#DC3545' : '#F26522' }}>{item.priority}</span>
      )}
      {meta.isVideoVisit && canVideo && (
        <button
          className="cal-row-video-btn"
          title="Join video visit"
          onClick={(e) => { e.stopPropagation(); joinVideoVisit(item, user, onAppNavigate); }}
        >
          <Video size={12} /> Join
        </button>
      )}
    </div>
  );
}

// Create the telehealth video room for this appointment and hand off to the
// Telehealth surface for a one-click join (reuses the existing video:use room).
async function joinVideoVisit(item, user, onAppNavigate) {
  try {
    // Opaque room label — must carry NO patient identifier. The room is
    // addressed by the server-generated opaque roomId; this human label is
    // persisted in video_rooms.room_name and audit_log, so embedding the
    // contact name (or the appointment title, which is itself PHI) would put a
    // plaintext identifier at rest for no functional gain. An opaque appointment
    // ref is enough for a human to recognize the room.
    const room = await window.zignagenixAPI.invoke('video:create-room', {
      patientId: item.contactId || null,
      roomName: `Telehealth visit${item.id ? ` #${item.id}` : ''}`,
      callType: 'video',
    });
    if (room && room.roomId) {
      try { sessionStorage.setItem('pendingVideoRoom', JSON.stringify(room)); } catch { /* private mode */ }
    }
    if (onAppNavigate) onAppNavigate('telehealth');
  } catch (e) {
    alert('Could not start the video visit: ' + (e?.message || 'unknown error'));
  }
}
